<template>
	<JBox class="">
		<!-- Header -->
		<JFlex class="s-bg-whites-w-full s-justify-between s-items-center s-p-4 s-border-b s-border-gray-200">
			<JHeadline class="s-w-1/3 s-text-xl s-text-gray-700"> {{ $t("uiEditor.pages") }} </JHeadline>
			<JFlex class="s-w-2/3 s-items-center s-justify-end">
				<JSpinner type="dots" v-if="isSaveLoading" class="s-mr-2" />
				<JButton variant="white" class="s-mr-2" @click="$emit('cancel')">Discard</JButton>
				<JButton variant="default-light" @click="handleSave">{{ $t("uiEditor.save") }}</JButton>
			</JFlex>
		</JFlex>

		<!-- Body -->
		<JBox class="s-bg-gray-100 s-mb-1 s-pb-16">
			<!-- Add Page -->
			<JFlex class="s-justify-start s-p-4">
				<JButton @click="addRoute" variant="default-light-sm">+ {{ $t("uiEditor.addNewPage") }} </JButton>
			</JFlex>

			<!-- Error -->
			<JBox class="s-bg-red-100 s-text-red-500 s-p-4 s-mb-2" v-if="errorMessage">
				{{ errorMessage }}
			</JBox>

			<JBox>
				<JDraggable :list="internalComponentRoutes" v-bind="dragOptions" :force-fallback="true">
					<JFlex
						v-for="(route, index) in internalComponentRoutes"
						:key="`route-${index}`"
						class="s-justify-between s-bg-white s-items-center s-mx-4 s-p-4 s-rounded-lg s-shadow-sm s-border s-border-gray-200 s-mb-2"
					>
						<JFlex class="s-w-auto">
							<JFlex class="s-w-3 s-items-center s-justify-center s-mr-4 draggable-handle">
								<CIcon name="draggable-handle" class="s-w-3 s-h-4 s-cursor-move s-fill-current s-text-gray-300" />
							</JFlex>
							<JBox>
								<JText class="s-w-full s-text-sm">{{ route.component }}</JText>
								<JText class="s-text-xs s-inline-block s-px-1 s-rounded s-bg-gray-200">{{ route.path }}</JText>
							</JBox>
						</JFlex>
						<JFlex class="s-w-24 s-justify-end">
							<JFlex
								class="s-items-center s-justify-center s-w-10 s-h-10 s-rounded-full s-bg-indigo-100 s-cursor-pointer"
								@click="initUpdate({ route, index })"
							>
								<CIcon name="pencil" class="s-h-5 s-w-5 s-fill-current s-text-indigo-600" />
							</JFlex>
						</JFlex>
					</JFlex>
				</JDraggable>
			</JBox>
		</JBox>

		<!-- Update Modal -->
		<Portal to="layer2">
			<CModal variant="default-small" :isVisible="isUpdateVisible" @overlay:click="isUpdateVisible = false">
				<JBox class="s-p-8">
					<AppPagesUpdateForm
						:value="routeToUpdate"
						@update="handleUpdate"
						@cancel="isUpdateVisible = false"
						@delete="handleDelete"
					/>
				</JBox>
			</CModal>
		</Portal>
	</JBox>
</template>

<script>
import AppPagesUpdateForm from "./AppPagesUpdateForm"
import { deepClone } from "@/utils/"

export default {
	components: { AppPagesUpdateForm },
	props: ["appId"],
	data() {
		return {
			// Internal state
			internalComponentRoutes: [],

			// Route template
			template: {
				component: "",
				name: "New Page",
				path: "/new-path"
			},
			dragOptions: {
				animation: 200,
				group: "pages",
				ghostClass: "s-opacity-0",
				handle: ".draggable-handle"
			},

			// Update
			isUpdateVisible: false,
			routeToUpdate: {},
			routeIndexToUpdate: {},

			isSaveLoading: false,
			errorMessage: ""
		}
	},

	methods: {
		addRoute() {
			const newTemplate = deepClone(this.template)
			this.internalComponentRoutes.unshift(newTemplate)
			this.initUpdate({ route: newTemplate, index: 0 })
		},

		initUpdate({ route, index }) {
			this.isUpdateVisible = true
			this.routeToUpdate = { ...route }
			this.routeIndexToUpdate = index
		},

		async handleSave() {
			if (this.isSaveLoading) return // handle double submit
			this.errorMessage = "" // reset

			this.isSaveLoading = true
			const { isSuccess, error } = await this.$store.dispatch("uiEditor/putComponentRoutes", {
				componentRoutes: this.internalComponentRoutes,
				appId: this.appId
			})
			this.isSaveLoading = false

			if (!isSuccess) {
				this.errorMessage = error
				return
			}

			this.$emit("done")
		},

		handleUpdate(newRoute) {
			this.internalComponentRoutes = this.internalComponentRoutes.map((route, index) => {
				if (this.routeIndexToUpdate === index) return newRoute
				return route
			})

			this.isUpdateVisible = false
			this.resetRouteToUpdate()
		},

		handleDelete() {
			this.internalComponentRoutes = this.internalComponentRoutes.filter((route, index) => index !== this.routeIndexToUpdate)
			this.isUpdateVisible = false
			this.resetRouteToUpdate()
		},

		resetRouteToUpdate() {
			this.routeToUpdate = null
			this.routeIndexToUpdate = null
		}
	},

	watch: {
		"$store.state.uiEditor.app.componentRoutes": {
			handler() {
				this.internalComponentRoutes = deepClone(this.$store.state.uiEditor.app?.componentRoutes)
			},
			deep: true,
			immediate: true
		}
	}
}
</script>
